<template>
    <div v-if="orderLine">
        <div class="row">
            <div class="col-12 mb-3"
                 :class="{'is-invalid' : errors.has('order_lines.' + lineIndex)}">
                <h5>{{ isNew ? (isRegularLine ? 'New product line' : 'New service line') :
                    (isRegularLine ? 'Edit product line' : 'Edit service line' ) }}</h5>
                <AppError :errors="errors" :field="'order_lines.' + lineIndex"/>
            </div>
        </div>
        <div class="row" v-if="isRegularLine">
            <p class="col-12 text-muted mb-3"><strong>General Info</strong></p>
        </div>
        <div class="row" v-if="isRegularLine">
            <AppSelect :value="orderLine.status"
                       :multiple="false"
                       :options="formattedOrderLineStatuses"
                       track-by="code"
                       label-field="name"
                       field="status_code"
                       label="Status"
                       class="col-md-6"
                       :disabled="orderLine.status_code === 'completed' || hasInvoiceSent"
                       @input="updateOrderLineProp({
                            status_code: $event ? $event.code  : null,
                            status: $event,
                       })"/>
        </div>
        <div class="row" v-if="isRegularLine">
            <AppDatePicker :value="orderLine.dispatched_at"
                           v-if="!isTreatment && !isAdjustment"
                           @input="updateOrderLineProp({ dispatched_at: $event })"
                           field="dispatched_at"
                           :allow-empty="true"
                           label="Dispatched at"
                           type="datetime"
                           class="col-md-6"
                           placeholder="Select date/time"
                           :disabled="hasInvoiceSent"
                           :errors="errors"/>
            <AppSelect :value="orderLine.dispatching_warehouse"
                       v-if="isTransfer || isTreatment || isDelivery || isSale"
                       :multiple="false"
                       :options="warehouseSuggestions"
                       track-by="id"
                       label-field="name"
                       label="Dispatching warehouse"
                       placeholder=""
                       @search-change="suggestWarehouse($event)"
                       @input="updateOrderLineProp({
                            dispatching_warehouse_id: $event ? $event.id : null,
                            dispatching_warehouse: $event,
                       })"
                       class="col-md-6"
                       :disabled="hasInvoiceSent"/>
        </div>
        <div class="row" v-if="isRegularLine">
            <AppDatePicker :value="orderLine.received_at"
                           v-if="isPurchase || isTransfer"
                           @input="updateOrderLineProp({ received_at: $event })"
                           field="received_at"
                           :allow-empty="true"
                           label="Received at"
                           type="datetime"
                           class="col-md-6"
                           placeholder="Select date/time"
                           :errors="errors"
                           :disabled="hasInvoiceSent"/>

            <AppSelect :value="orderLine.receiving_warehouse"
                       v-if="isPurchase || isTransfer || isAdjustment"
                       :multiple="false"
                       :options="warehouseSuggestions"
                       track-by="id"
                       label-field="name"
                       label="Receiving warehouse"
                       placeholder=""
                       @search-change="suggestWarehouse($event)"
                       @input="updateOrderLineProp({
                            receiving_warehouse_id: $event ? $event.id : null,
                            receiving_warehouse: $event,
                       })"
                       class="col-md-6"
                       :disabled="hasInvoiceSent"/>
        </div>
        <div v-if="isRegularLine" class="row">
            <AppInput :value="orderLine.job_density"
                      v-if="isTreatment || isDelivery"
                      @input="updateOrderLineProp({ job_density: $event })"
                      :errors="errors"
                      class="col-md-3"
                      label="PPM"
                      field="job_density"
                      :disabled="hasInvoiceSent"/>
            <AppInput :value="orderLine.job_spec_before"
                      v-if="isTreatment"
                      @input="updateOrderLineProp({ job_spec_before: $event })"
                      :errors="errors"
                      class="col-md-3"
                      label="Blank"
                      field="job_spec_before"
                      :disabled="hasInvoiceSent"/>
            <AppInput :value="orderLine.job_spec_required"
                      v-if="isTreatment"
                      @input="updateOrderLineProp({ job_spec_required: $event })"
                      :errors="errors"
                      class="col-md-3"
                      label="Spec required"
                      field="job_spec_required"
                      :disabled="hasInvoiceSent"/>
            <AppInput :value="orderLine.job_spec_after"
                      v-if="isTreatment"
                      @input="updateOrderLineProp({ job_spec_after: $event })"
                      :errors="errors"
                      class="col-md-3"
                      label="Result"
                      field="job_spec_after"
                      :disabled="hasInvoiceSent"/>
        </div>
        <div class="row">
            <p class="col-12 text-muted mb-3"><strong>
                {{ isRegularLine ? 'Product Info' : 'Service Info'}}
            </strong></p>
        </div>
        <div class="row">
            <div class="col-md-8">
                <AppSelect :value="orderLine.product"
                           :multiple="false"
                           :options="productSuggestions"
                           track-by="id"
                           label-field="name"
                           :errors="errors"
                           :field="'order_lines.' + lineIndex + '.product_id'"
                           :label="isRegularLine ? 'Product' : 'Service'"
                           placeholder=""
                           @open="suggestProducts(null)"
                           @search-change="suggestProducts($event)"
                           @input="updateOrderLineProp({
                                product: $event ? $event : null,
                                product_id: $event ? $event.id : null,
                           })"
                           :disabled="hasInvoiceSent"/>
            </div>
        </div>
        <div class="row">
            <AppInput :value="orderLine.quantity"
                      @input="updateOrderLineProp({ quantity: $event })"
                      :errors="errors"
                      class="col-md-4"
                      type="number"
                      label="Quantity"
                      :field="'order_lines.' + lineIndex + '.quantity'"
                      v-if="isRegularLine"
                      :disabled="hasInvoiceSent"/>
            <AppInput :value="orderLine.price"
                      @input="updateOrderLineProp({ price: $event })"
                      :errors="errors"
                      class="col-md-4"
                      type="number"
                      label="Price"
                      :field="'order_lines.' + lineIndex + '.price'"
                      v-if="isAdjustment"
                      :disabled="hasInvoiceSent"/>
            <AppInput :value="orderLine.price"
                      @input="updateOrderLineProp({ price: $event })"
                      :errors="errors"
                      class="col-md-4"
                      type="number"
                      :disabled="orderLine.status_code === 'completed' || hasInvoiceSent"
                      v-if="isPurchase || !isRegularLine"
                      :label="isRegularLine
                      ? 'Price per unit (€)' : 'Service price'" field="price"/>
            <AppSelect :value="orderLine.unit_type"
                       :multiple="false"
                       :options="orderUnitTypes"
                       track-by="code"
                       label-field="name"
                       label="Unit type"
                       class="col-md-4"
                       v-if="isRegularLine"
                       placeholder=""
                       @input="updateOrderLineProp({
                            unit_type_code: $event? $event.code: null,
                            unit_type: $event,
                       })"
                       :disabled="hasInvoiceSent"/>
        </div>
        <div class="row" v-if="isRegularLine">
            <AppSelect :value="orderLine.package"
                       :multiple="false"
                       :options="packages"
                       track-by="id"
                       label-field="name"
                       label="Package"
                       class="col-md-7"
                       placeholder=""
                       @input="updateOrderLineProp({
                            package_id: $event ? $event.id: null,
                            package: $event,
                       })"
                       :disabled="hasInvoiceSent"/>
            <AppInput :value="orderLine.package_quantity"
                      @input="updateOrderLineProp({ package_quantity: $event })"
                      :errors="errors"
                      label="Package quantity"
                      class="col-md-4"
                      type="number"
                      field="package_quantity"
                      :disabled="hasInvoiceSent"/>
        </div>
        <div class="row" v-if="isRegularLine && (isTreatment || isDelivery || isSale)">
            <AppSelect :value="orderLine.job_treatment_category"
                       :multiple="false"
                       :options="treatmentCategories"
                       track-by="id"
                       label-field="name"
                       label="Treatment category"
                       class="col-md-7"
                       placeholder=""
                       @input="updateOrderLineProp({
                            job_treatment_category_id: $event ? $event.id: null,
                            job_treatment_category: $event,
                       })"
                       :disabled="hasInvoiceSent"/>
        </div>
        <div class="row mt-3">
            <div class="col-12 d-flex justify-content-between">
                <button class="btn btn-outline-danger mr-2"
                        @click="destroy"
                        :disabled="hasInvoiceSent">Delete</button>
                <button class="btn btn-primary" @click="close">Done</button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import debounce from 'lodash.debounce';
import AppInput from '../form/AppInput';
import AppSelect from '../form/AppSelect';
import AppDatePicker from '../form/AppDatePicker';
import AppError from '../form/AppError';

export default {
  props: {
    lineType: String,
    orderType: String,
  },
  components: {
    AppDatePicker,
    AppInput,
    AppSelect,
    AppError,
  },
  data() {
    return {
      productSuggestions: [],
      warehouseSuggestions: [],
    };
  },
  computed: {
    ...mapGetters({
      order: 'orders/order',
      orderLine: 'orderLines/orderLine',
      orderLineStatuses: 'orderLines/orderLineStatuses',
      packages: 'packages/productPackages',
      orderUnitTypes: 'unitTypes/orderUnitTypes',
      treatmentCategories: 'treatmentCategories/treatmentCategories',
    }),
    ...mapState({
      lineIndex: (state) => state.orderLines.lineIndex,
      errors: (state) => state.orderLines.orderErrors,
    }),
    formattedOrderLineStatuses() {
      const orderStatuses = [...this.orderLineStatuses];
      return orderStatuses.map((orderStatus) => {
        if (orderStatus.code === 'in_transit') {
          if (this.isTreatment || this.isDelivery || this.isSale) {
            orderStatus.name = 'In Progress';
          } else if (this.isTransfer || this.isPurchase) {
            orderStatus.name = 'In Transit';
          }
        }
        return orderStatus;
      });
    },
    isNew() {
      return this.$store.state.orderLines.isNewOrderLine;
    },
    isOrderNew() {
      return this.$route.params.id === 'new';
    },
    hasInvoice() {
      return this.order.has_invoice;
    },
    hasInvoiceSent() {
      return this.order.has_invoice_sent;
    },
    isRegularLine() {
      return this.lineType === 'regular';
    },
    isPurchase() {
      return this.orderType === 'purchase';
    },
    isAdjustment() {
      return this.orderType === 'adjustment';
    },
    isTransfer() {
      return this.orderType === 'transfer';
    },
    isTreatment() {
      return this.orderType === 'treatment';
    },
    isDelivery() {
      return this.orderType === 'delivery';
    },
    isSale() {
      return this.orderType === 'sale';
    },
  },
  methods: {
    suggestWarehouse: debounce(function suggest(query) {
      const params = {
        query,
      };
      return this.$store.dispatch('warehouses/suggestWarehouses', params)
        .then((suggestions) => {
          this.warehouseSuggestions = suggestions;
        });
    }, 300),
    suggestProducts: debounce(function suggest(query) {
      const params = {
        query,
      };

      if (this.isRegularLine) {
        params.avoid_type = 'service';
      } else {
        params.type = 'service';
      }

      return this.$store.dispatch('products/suggestProducts', params)
        .then((suggestions) => {
          this.productSuggestions = suggestions;
        });
    }, 300),
    close() {
      if (this.isOrderNew) {
        this.getOrderLinePrice();
      }
      this.$store.commit('orderLines/isModalOpen', false);
    },
    destroy() {
      this.$store.dispatch('orderLines/deleteOrderLine');
      this.$store.commit('orderLines/isModalOpen', false);
    },
    updateOrderLineProp(props) {
      if (props.hasOwnProperty('status_code') && props.status_code === 'completed') {
        this.$bvModal.msgBoxConfirm('This action cannot be reversed, as it will trigger stock calculation.', {
          title: 'Set the status to complete?',
          okTitle: 'Set as Completed',
          okVariant: 'danger',
        })
          .then(() => this.$store.dispatch('orderLines/updateOrderLineProps', props));
      } else {
        this.$store.dispatch('orderLines/updateOrderLineProps', props);
      }
    },
    getOrderLinePrice() {
      if (this.orderLine && this.orderLine.dispatching_warehouse_id) {
        const props = {
          order_line_id: this.orderLine.id,
          currency: this.orderLine.order ? this.orderLine.order.currency.code : null,
          received_at: this.orderLine.received_at,
          warehouse_id: this.orderLine.dispatching_warehouse_id,
          product_id: this.orderLine.product_id,
          quantity: this.orderLine.quantity,
        };

        if (this.orderLine.unit_type_code === 'ltr') {
          props.unit_type = this.orderLine.unit_type_code;
        }

        this.$store.dispatch('orders/getOrderLinePrice', props);
      }
    },
  },
};
</script>
